import {
  QuickKeyColour,
  QuickKeyNodeType,
  DEFAULT_COLUMNS,
  DEFAULT_ROWS,
} from './qk.constants'
import {
  ApiLayoutData,
  ApiNode,
  AttributeKey,
  PageDimensions,
  QuickKey,
  QuickKeyBlank,
  QuickKeyFolder,
  QuickKeyKey,
  QuickKeyPage,
  QuickKeyRoot,
  QuickKeyPageChild,
  QuickKeyPageNode,
} from './qk-types'

const PLACEHOLDER_PAGE_PREFIX = 'qk-placeholder-page-'

enum PadBlanks {
  END_OF_ROW,
  BLANK_ROW,
  FULL_PAGE,
}

interface Options {
  padEmptyRows?: boolean
  lockedQuickKeys?: string[]
}

/**
 * Build the tree representation of the given collection of quick key nodes.
 *
 * @param  qkLayoutData
 *         Quick key layout data as returned by the API
 *
 * @param  [options]
 *
 *         @param [options.padEmptyRows=false]
 *                Whether to pad out the last page of each folder/grid with empty rows (default behaviour is to just
 *                pad out the last populated row with blank nodes, not the whole page)
 *
 *         @param [options.lockedQuickKeys]
 *                Array of quick key IDs representing those quick keys that should be flagged as locked
 *
 * @return {Object} The root node of the tree containing the layout metadata (`id`, `name`) and an array of the rest
 *         of the tree structure (`children`)
 */
export const buildQuickKeysLayout = function (
  qkLayoutData: ApiLayoutData,
  options: Options = {}
): QuickKeyRoot {
  const nodesByParentId = mapNodes(qkLayoutData)
  const rootNode = {
    id: qkLayoutData.id,
    product_id: null,
    name: qkLayoutData.name,
    attributes: [],
    parent_node_id: null,
    position: 0,
  }

  return buildTree({
    node: rootNode,
    nodesByParentId,
    parentNode: undefined,
    options,
  })
}

/**
 * Find the given folder in the given updated layout or return null if it no longer exists. This can be used to help
 * maintain the state of the view (e.g. keep the folder open etc) when the quick key layout is updated after a data
 * sync.
 *
 * @param  folder {Object}
 *         The quick key tree node for the folder to find
 *
 * @param  originalLayoutId {String}
 *         The ID of the original layout that the folder belonged to
 *
 * @param  updatedLayout {Object}
 *         The root node of the updated quick key layout
 *
 * @return {Object} The quick key tree node for the matching folder in the updated quick key layout, or `null` if it
 *         could not be found
 */
export const findFolderInUpdatedLayout = function (
  folder: QuickKeyFolder,
  originalLayoutId: string,
  updatedLayout: QuickKeyRoot
) {
  if (originalLayoutId !== updatedLayout.node.id) {
    return null
  }

  const folderId = folder.node.id
  let foundFolder = null

  updatedLayout.children.some(pageNode => {
    const found = pageNode.children.find(
      folderNode => folderId === folderNode.node.id
    )
    if (found) {
      foundFolder = found
    }
    return found
  })

  return foundFolder
}

/**
 * Get the product details (suitable to emit as an event) of the given quick key.
 *
 * @method getProductDetails
 *
 * @param  quickKey
 *         The quick key tree node to get the product details for
 *
 * @return {Object} The product details of the given quick key
 */
export const getProductDetails = function (quickKey: QuickKey) {
  const node = quickKey.node

  return {
    label: getAttrValue('label', node),
    productId: node.product_id,
    isChild: getBooleanAttrValue('isChild', node),
  }
}

/**
 * Determine whether the given page node is a placeholder page and needs to be created via the API before adding a
 * quick key to it.
 *
 * @param  pageNode
 *         The JSON representation of the page node
 *
 * @return true if the given page node is a placeholder, false otherwise
 */
export const isPlaceholderPage = function (
  pageNode?: ApiNode | undefined | null
) {
  const pageNodeId = pageNode && pageNode.id
  return Boolean(pageNodeId && pageNodeId.startsWith(PLACEHOLDER_PAGE_PREFIX))
}

/**
 * Get the value of the specified attribute from the given quick key.
 *
 * @param  attributeName
 *         The name of the attribute to look up
 *
 * @param  node
 *         The quick key node (as returned by the API) to look up the attribute from
 *
 * @return The value of the specified attribute or `undefined` if it could not be found
 */
const getAttrValue = function (
  attributeName: AttributeKey,
  node: ApiNode
): string | undefined {
  if (node && node.attributes) {
    for (const attr of node.attributes) {
      if (attr.key === attributeName) {
        return attr.value
      }
    }
  }
}

const getColour = (node: ApiNode): QuickKeyColour => {
  return getAttrValue('color', node) as QuickKeyColour
}

/**
 * Get the Boolean value of the specified attribute from the given quick key. Boolean attributes are stored as '1' or
 * '0' in the API.
 *
 * @param  attributeName
 *         The name of the boolean attribute to look up
 *
 * @param  node
 *         The quick key node (as returned by the API) to look up the attribute from
 *
 * @return The value of the specified attribute
 */
const getBooleanAttrValue = function (
  attributeName: AttributeKey,
  node: ApiNode
) {
  return Boolean(Number(getAttrValue(attributeName, node)))
}

/**
 * Build the quick key tree for the given node (either a ROOT or FOLDER node).
 *
 * @param  params {Object}
 *
 *         @param params.node {Node}
 *                The raw JSON representation of a quick key node from the API
 *
 *         @param params.nodesByParentId {Object}
 *                The map of parent node ID to array of nodes with that parent
 *
 *         @param [params.parentNode] {Node}
 *                The given node's parent node (applicable if we're building the tree for a folder node)
 *
 *         @param [params.options] {Object}
 *
 *                @param [params.options.padEmptyRows=false] {Boolean}
 *                       Whether to pad out the last page of each folder/grid with empty rows (default behaviour is
 *                       to just pad out the last populated row with blank nodes, not the whole page)
 *
 *                @param [params.options.lockedQuickKeys] {Array<String>}
 *                       Array of quick key IDs representing those quick keys that should be flagged as locked
 *
 * @return {Object} The tree node with a `node` property referencing the given node and a `children` array of all
 *         the tree nodes that are a child of this node
 */
const buildTree = function <PN extends ApiNode | undefined>({
  node,
  nodesByParentId,
  parentNode,
  options,
}: {
  node: ApiNode
  nodesByParentId: Record<string, ApiNode[]>
  parentNode?: PN
  options: Options
}): PN extends ApiNode ? QuickKeyFolder : QuickKeyRoot {
  const { padEmptyRows } = options
  const pageNodes = nodesByParentId[node.id] || []
  const type = parentNode ? QuickKeyNodeType.FOLDER : QuickKeyNodeType.ROOT

  let layerFull = false
  if (padEmptyRows) {
    let lastPageIsFull

    layerFull = pageNodes.every((pageNode, index) => {
      const { pageSize, rowSize } = getPageDimensions(pageNode)
      const numberOfQuickKeys = (nodesByParentId[pageNode.id] || []).length
      const pageIsFull = numberOfQuickKeys === pageSize

      if (index === pageNodes.length - 1) {
        lastPageIsFull = pageIsFull
        return pageIsFull || numberOfQuickKeys % rowSize === 0
      } else {
        return pageIsFull
      }
    })

    if (!pageNodes.length || (layerFull && lastPageIsFull)) {
      // If the current layer has no pages or is completely saturated with quick keys, add a blank placeholder page
      pageNodes.push(
        createPlaceholderPage(node, type, {
          position: pageNodes.length,
        })
      )
    }
  }

  const children = pageNodes
    .sort((node1, node2) => node1.position - node2.position)
    .map((pageNode, pageIndex, siblings) => {
      return buildPage({
        node: pageNode,
        parentNode: node,
        parentNodeType: type,
        nodesByParentId,
        pageIndex,
        numberOfPages: siblings.length,
        layerFull,
        options,
      })
    })

  let viewData: QuickKeyFolder['viewData'] | undefined
  if (type === QuickKeyNodeType.FOLDER) {
    viewData = {
      folderSize: children.reduce(
        (size, pageNode) => size + pageNode.children.length,
        0
      ),
      label: getAttrValue('label', node)!,
      color: getColour(node),
    }
  }

  // "as any" because the compiler can't infer conditional types :(
  return { type, node, parentNode, children, viewData } as any
}

/**
 * Build the quick key page for the given node.
 *
 * @param  params {Object}
 *
 *         @param params.node {Node}
 *                The raw JSON representation of a quick key node from the API
 *
 *         @param params.nodesByParentId {Object}
 *                The map of parent node ID to array of nodes with that parent
 *
 *         @param params.parentNode {Node}
 *                The given node's parent node
 *
 *         @param params.parentNodeType {String}
 *                A string representing the parent node type.
 *
 *         @param params.pageIndex {Number}
 *                The index of this page within the array of all page at the same layer
 *
 *         @param params.numberOfPages {Number}
 *                The total number of all pages at the same layer
 *
 *         @param params.layerFull {Boolean}
 *                Whether or not the current layer (top level or folder) is full i.e. has all slots populated with a
 *                quick key or folder
 *
 *         @param [params.options] {Object}
 *
 *                @param [params.options.padEmptyRows=false] {Boolean}
 *                       Whether to pad out the last page of each folder/grid with additional blank nodes (default
 *                       behaviour is to just pad out the last populated row with blank nodes)
 *
 *                @param [params.options.lockedQuickKeys] {Array<String>}
 *                       Array of quick key IDs representing those quick keys that should be flagged as locked
 *
 * @return {Object} The page node with a `node` property referencing the given node and a `children` array of all
 *         the tree nodes that are a child of this node
 */
const buildPage = function ({
  node,
  nodesByParentId,
  parentNode,
  parentNodeType,
  pageIndex,
  numberOfPages,
  layerFull,
  options,
}: {
  node: ApiNode
  nodesByParentId: Record<string, ApiNode[]>
  parentNode: ApiNode
  parentNodeType: QuickKeyNodeType
  pageIndex: number
  numberOfPages: number
  layerFull: boolean
  options: Options
}): QuickKeyPage {
  const { padEmptyRows, lockedQuickKeys } = options
  const quickKeyNodes = nodesByParentId[node.id] || []

  const children: QuickKeyPage['children'] = quickKeyNodes
    .sort((node1, node2) => node1.position - node2.position)
    .map(quickKeyNode => {
      const config = {
        node: quickKeyNode,
        parentNode: node,
        nodesByParentId,
        options,
      }

      return quickKeyNode.product_id ? buildQuickKey(config) : buildTree(config)
    })

  const pageDimensions = getPageDimensions(node)

  let padBlanks = PadBlanks.END_OF_ROW
  if (pageIndex < numberOfPages - 1) {
    // Not the last page, fill to end of page with blanks
    padBlanks = PadBlanks.FULL_PAGE
  } else if (padEmptyRows) {
    if (pageIndex === 0) {
      // Always pad the first page with blanks to the end of the page
      padBlanks = PadBlanks.FULL_PAGE
    } else if (layerFull) {
      // If the rest of the current layer (top level or folder) is full, add an extra row of blank quick keys
      padBlanks = PadBlanks.BLANK_ROW
    }
  }

  const nodes = getPageNodes(node, children, {
    padBlanks,
    pageDimensions,
  })
  const pageColumns = getPageColumns(nodes, pageDimensions.rowSize)

  const viewData: QuickKeyPage['viewData'] = {
    pageDimensions,
    nodes,
    pageColumns,
    numberOfPages,
    pageIndex,
  }

  if (parentNodeType === QuickKeyNodeType.ROOT && pageIndex === 0) {
    viewData.close_folder = getBooleanAttrValue('close_folder', node)
  }
  const page: QuickKeyPage = {
    type: QuickKeyNodeType.PAGE,
    node,
    parentNode,
    children,
    viewData,
  }

  _updateLocks(page, lockedQuickKeys)

  return page
}

/**
 * Build the quick key for the given node.
 *
 * @param  params {Object}
 *
 *         @param params.node {Node}
 *                The raw JSON representation of a quick key node from the API
 *
 *         @param params.parentNode {Node}
 *                The given node's parent node
 *
 * @return {Object} The quick key node with a `node` property referencing the given node
 */
const buildQuickKey = function ({
  node,
  parentNode,
}: {
  node: ApiNode
  parentNode: ApiNode
}): QuickKeyKey {
  const viewData: QuickKeyKey['viewData'] = {
    label: getAttrValue('label', node)!,
    color: getColour(node),
    showImage: getBooleanAttrValue('showImage', node),
  }

  return { type: QuickKeyNodeType.QUICK_KEY, node, parentNode, viewData }
}

/**
 * Get the nodes (including gaps filled with blank nodes) to render a page of quick keys.
 *
 * @param  pageNode {Object}
 *         The node representing the page of quick keys (as returned by the API)
 *
 * @param  children {Array}
 *         The array of nodes that are children of the page
 *
 * @param  options
 *
 *         @param [options.padBlanks = 'END_OF_ROW'] {String}
 *                How much of the remaining page to pad out with blanks - to the end of the last row, one whole blank
 *                row, or to the end of the page.
 *
 *         @param options.pageDimensions {Object}
 *                Data on the dimensions (rowSize, columnSize, pageSize) of the given page node
 *
 * @return {Array} The array of nodes, including blank nodes, that make up the given page of quick keys
 */
const getPageNodes = function (
  pageNode: ApiNode,
  children: QuickKeyPageChild[],
  {
    padBlanks,
    pageDimensions,
  }: {
    padBlanks: PadBlanks
    pageDimensions: PageDimensions
  }
) {
  const nodes: Array<QuickKeyPageChild | QuickKeyBlank> = []
  const { pageSize, rowSize } = pageDimensions

  children.forEach(treeNode => {
    const { position } = treeNode.node
    fillBlanksToSize(pageNode, nodes, position)
    nodes.push(treeNode)
  })

  if (padBlanks === PadBlanks.FULL_PAGE) {
    if (nodes.length < pageSize) {
      fillBlanksToSize(pageNode, nodes, pageSize)
    }
  } else {
    const sizeOfLastRow = nodes.length % rowSize

    if (sizeOfLastRow !== 0 || padBlanks === PadBlanks.BLANK_ROW) {
      const blanksToFillRow = rowSize - sizeOfLastRow
      fillBlanksToSize(pageNode, nodes, nodes.length + blanksToFillRow)
    }
  }

  return nodes
}

/**
 * Break the given array of nodes out into an array of columns, each being an array of the nodes in those columns.
 *
 * @param  pageNodes
 *         The array of nodes present on the page of quick keys
 *
 * @param  rowSize
 *         The number of quick keys in a row (i.e. the number of columns on a page)
 *
 * @return The same collection of quick key nodes broken down into an array of column arrays
 */
const getPageColumns = function (
  pageNodes: QuickKeyPageNode[],
  rowSize: number
) {
  const columns = []

  for (let colIndex = 0; colIndex < rowSize; colIndex += 1) {
    const column = []

    for (
      let nodeIndex = colIndex;
      nodeIndex < pageNodes.length;
      nodeIndex += rowSize
    ) {
      column.push(pageNodes[nodeIndex])
    }

    columns.push(column)
  }

  return columns
}

/**
 * Creates a blank placeholder page to be used until the first quick key is added to the page (and the page is
 * created for real via the API).
 *
 * @param  parentNode
 *         The parent node of the placeholder page (either the root node or a folder node)
 *
 * @param  parentNodeType
 *         The type of the parent node (ROOT or FOLDER)
 *
 * @return The JSON representation of a page suitable to use as a placeholder
 */
const createPlaceholderPage = function (
  parentNode: ApiNode,
  parentNodeType: QuickKeyNodeType,
  {
    position,
  }: {
    position: number
  }
): ApiNode {
  return {
    id: `${PLACEHOLDER_PAGE_PREFIX}${parentNode.id}`,
    parent_node_id:
      parentNodeType === QuickKeyNodeType.ROOT ? null : parentNode.id,
    position,
    product_id: null,
    attributes: [
      {
        key: 'columns',
        value: `${DEFAULT_COLUMNS}`,
      },
      {
        key: 'rows',
        value: `${DEFAULT_ROWS}`,
      },
    ],
  }
}

/**
 * Fill the given array of quick key nodes with blank nodes until the array is the given size.
 *
 * @param  pageNode
 *         The node for the page of quick keys the blank quick keys are a part of (for unique ID generation)
 *
 * @param  nodes
 *         The array of quick key nodes
 *
 * @param  size
 *         The size of the array to fill up to
 */
const fillBlanksToSize = function (
  pageNode: ApiNode,
  nodes: QuickKey[],
  size: number
) {
  for (let position = nodes.length; position < size; position += 1) {
    const blank: QuickKeyBlank = {
      type: QuickKeyNodeType.BLANK,
      viewData: {
        color: QuickKeyNodeType.BLANK,
      },
      node: {
        id: `${pageNode.id}-blank-${position}`,
        product_id: null,
        attributes: [],
        parent_node_id: pageNode.id,
        position,
      },
      parentNode: pageNode,
    }

    nodes.push(blank)
  }
}

/**
 * Get the dimensions of a given page. Dimensions include:
 *
 * - `rowSize` (i.e. the number of columns). Defaults to 5.
 * - 'columnSize' (i.e. the number of columns). Defaults to 5.
 * - 'pageSize'(i.e. max number of quick keys it can contain). Defaults to 25.
 *
 * @param  pageNode
 *         The node representing the page of quick keys (as returned by the API)
 *
 * @return Object containing the `pageSize`, `rowSize` and `columnSize` dimensions of the given page node
 */
const getPageDimensions = function (pageNode: ApiNode) {
  const rowSize = Number(getAttrValue('columns', pageNode)) || DEFAULT_COLUMNS
  const columnSize = Number(getAttrValue('rows', pageNode)) || DEFAULT_ROWS
  const pageSize = rowSize * columnSize

  return { pageSize, rowSize, columnSize }
}

/**
 * Map quick key nodes by their parent node's ID and identify the root nodes (those with no parent).
 *
 * @param  nodes
 *         Array of nodes to map by their parent node IDs
 *
 * @return Object with a `rootNodes` property containing the array of root nodes, and a `nodesByParentId`
 *         property containing the collection of nodes mapped by their parent node's ID
 */
const mapNodes = function (quickKeyData: ApiLayoutData) {
  const nodesByParentId: Record<string, ApiNode[]> = {}

  quickKeyData.nodes.forEach(qkNode => {
    const parentId = qkNode.parent_node_id || quickKeyData.id

    let siblings = nodesByParentId[parentId]
    if (!siblings) {
      siblings = []
      nodesByParentId[parentId] = siblings
    }
    siblings.push(qkNode)
  })

  return nodesByParentId
}

/**
 * Set the locked property of the provided quick key.
 *
 * @param  rootNode
 *         The root node to evaluate
 *
 * @param  lockedQuickKeys
 *         Collection of locked quick keys
 */
const _updateLocks = function (
  rootNode: QuickKey,
  lockedQuickKeys: string[] | undefined
) {
  const nodes =
    rootNode.type === QuickKeyNodeType.PAGE && rootNode.viewData.nodes
  if (nodes) {
    nodes.forEach(quickKey => {
      quickKey.locked = Boolean(
        lockedQuickKeys && lockedQuickKeys.indexOf(quickKey.node.id) !== -1
      )
    })
  }
}
